import { useState, useCallback, useMemo, lazy, Suspense, useEffect } from 'react';
import { Graph2D } from './components/Graph2D';
import { StatsPanel } from './components/StatsPanel';
import { FilterPanel } from './components/FilterPanel';
import { Controls } from './components/Controls';
import { Legend } from './components/Legend';
import { useGraphData } from './hooks/useGraphData';
import { useClusteredGraph } from './hooks/useClusteredGraph';
import type { ViewMode, ForceGraphData } from './types/graph';

const Graph3D = lazy(() => import('./components/Graph3D').then(m => ({ default: m.Graph3D })));

function buildAdjacency(data: ForceGraphData) {
  const forward = new Map<string, Set<string>>();
  const backward = new Map<string, Set<string>>();

  for (const link of data.links) {
    const src = typeof link.source === 'object' ? (link.source as any).id : link.source;
    const tgt = typeof link.target === 'object' ? (link.target as any).id : link.target;

    if (!forward.has(src)) forward.set(src, new Set());
    forward.get(src)!.add(tgt);

    if (!backward.has(tgt)) backward.set(tgt, new Set());
    backward.get(tgt)!.add(src);
  }

  return { forward, backward };
}

function bfs(start: string, adj: Map<string, Set<string>>): Set<string> {
  const visited = new Set<string>();
  const queue = [start];
  visited.add(start);

  while (queue.length > 0) {
    const current = queue.shift()!;
    const neighbors = adj.get(current);
    if (neighbors) {
      for (const n of neighbors) {
        if (!visited.has(n)) {
          visited.add(n);
          queue.push(n);
        }
      }
    }
  }

  return visited;
}

function App() {
  const [viewMode, setViewMode] = useState<ViewMode>('2d');
  const [preload3D, setPreload3D] = useState(false);
  const [clusterMode, setClusterMode] = useState(false);
  const [highlightNodes, setHighlightNodes] = useState<Set<string> | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);

  const {
    graphData,
    rawData,
    loading,
    error,
    filters,
    availableServices,
    availableLanguages
  } = useGraphData();

  const { clusteredData, toggleCluster } = useClusteredGraph(graphData, clusterMode);
  const effectiveClusterMode = clusterMode && viewMode === '2d';
  const displayData = effectiveClusterMode ? clusteredData : graphData;
  const adjacency = useMemo(() => buildAdjacency(displayData), [displayData]);

  useEffect(() => {
    if (!loading && graphData.nodes.length > 0) {
      const timer = setTimeout(() => setPreload3D(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [loading, graphData.nodes.length]);

  const handleNodeClick = useCallback((node: any) => {
    if (!node) {
      setHighlightNodes(null);
      return;
    }

    if (node._isCluster && node._clusterKey) {
      toggleCluster(node._clusterKey);
      setHighlightNodes(null);
      return;
    }

    const forwardSet = bfs(node.id, adjacency.forward);
    const backwardSet = bfs(node.id, adjacency.backward);
    const combined = new Set<string>();
    for (const id of forwardSet) combined.add(id);
    for (const id of backwardSet) combined.add(id);
    setHighlightNodes(combined);
  }, [adjacency, toggleCluster]);

  const handleClearHighlight = useCallback(() => {
    setHighlightNodes(null);
  }, []);

  const handleExport = useCallback(() => {
    const canvas = document.querySelector('canvas');
    if (!canvas) return;

    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `nilassist-architecture-${viewMode}-${Date.now()}.png`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    });
  }, [viewMode]);

  if (loading) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-bg)',
        color: 'var(--color-text-primary)',
        fontFamily: 'var(--font-family)',
        fontSize: 'var(--font-size-xl)'
      }}>
        Loading dependency graph...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-bg)',
        color: 'var(--color-danger)',
        fontFamily: 'var(--font-family)',
        fontSize: 'var(--font-size-xl)'
      }}>
        Error loading graph: {error}
      </div>
    );
  }

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      position: 'relative',
      overflow: 'hidden',
      background: 'var(--color-bg)'
    }}>
      {/* Layer 0: Canvas */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          visibility: viewMode === '2d' ? 'visible' : 'hidden',
          pointerEvents: viewMode === '2d' ? 'auto' : 'none'
        }}>
          <Graph2D
            data={displayData}
            highlightNodes={highlightNodes}
            onNodeClick={handleNodeClick}
          />
        </div>

        {preload3D && (
          <Suspense fallback={
            viewMode === '3d' ? (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--color-bg)',
                color: 'var(--color-text-primary)',
                fontSize: 'var(--font-size-xl)'
              }}>
                Loading 3D view...
              </div>
            ) : null
          }>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              visibility: viewMode === '3d' ? 'visible' : 'hidden',
              pointerEvents: viewMode === '3d' ? 'auto' : 'none'
            }}>
              <Graph3D
                data={displayData}
                highlightNodes={highlightNodes}
                onNodeClick={handleNodeClick}
              />
            </div>
          </Suspense>
        )}
      </div>

      {/* Layer 1: HUD */}
      <div className="hud">
        {/* Header */}
        <div className="hud-header">
          <div style={{
            fontSize: 'var(--font-size-2xl)',
            fontWeight: 700,
            fontFamily: 'var(--font-family-brand)',
            letterSpacing: '-0.03125rem',
            textShadow: '0 0.125rem 0.625rem rgba(0,0,0,0.5)',
            whiteSpace: 'nowrap'
          }}>
            <span style={{ color: 'var(--color-brand)' }}>NILA</span>
            <span style={{ color: '#F5EEE1' }}>ssist</span>
            <span style={{ color: '#F5EEE1', fontSize: '0.4em', verticalAlign: 'super' }}>®</span>
            {' '}
            <span style={{ color: 'var(--color-brand)' }}>Architecture</span>
          </div>
          <p style={{
            margin: 0,
            fontSize: 'var(--font-size-lg)',
            fontWeight: 500,
            color: 'var(--color-text-primary)',
            fontFamily: 'var(--font-family)',
            textShadow: '0 0.0625rem 0.3125rem rgba(0,0,0,0.5)',
            letterSpacing: '0.03125rem'
          }}>
            {displayData.nodes.length > 0
              ? `${new Set(displayData.nodes.map(n => n.service)).size} services · ${displayData.nodes.length} files · ${displayData.links.length} dependencies`
              : 'Loading...'}
          </p>
        </div>

        {/* Left Drawers (flex column — filter top, legend bottom, share space) */}
        <div className="drawers-left">
          <div className={`drawer drawer--left drawer--filter${filterOpen ? '' : ' closed'}`}>
            <div className="drawer-body">
              <FilterPanel
                services={availableServices}
                languages={availableLanguages}
                selectedServices={filters.selectedServices}
                selectedLanguages={filters.selectedLanguages}
                searchQuery={filters.searchQuery}
                onServicesChange={filters.setSelectedServices}
                onLanguagesChange={filters.setSelectedLanguages}
                onSearchChange={filters.setSearchQuery}
              />
            </div>
            <div
              className={`pull-tab${filterOpen ? ' active' : ''}`}
              onClick={() => setFilterOpen(v => !v)}
            >
              Filters
            </div>
          </div>

          <div className={`drawer drawer--left drawer--legend${legendOpen ? '' : ' closed'}`}>
            <div className="drawer-body">
              <Legend />
            </div>
            <div
              className={`pull-tab${legendOpen ? ' active' : ''}`}
              onClick={() => setLegendOpen(v => !v)}
            >
              Services
            </div>
          </div>
        </div>

        {/* Stats Drawer (right, full height, slides from right) */}
        <div className={`drawer drawer--right drawer--stats${statsOpen ? '' : ' closed'}`}>
          <div className="drawer-body">
            <StatsPanel rawData={rawData} filteredData={displayData} />
          </div>
          <div
            className={`pull-tab${statsOpen ? ' active' : ''}`}
            onClick={() => setStatsOpen(v => !v)}
          >
            Stats
          </div>
        </div>

        {/* Controls */}
        <div className="hud-controls">
          <Controls
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onExport={handleExport}
            clusterMode={clusterMode}
            onClusterModeChange={setClusterMode}
            highlightActive={highlightNodes !== null && highlightNodes.size > 0}
            onClearHighlight={handleClearHighlight}
          />
        </div>
      </div>
    </div>
  );
}

export default App;
